import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { Loader2, Check } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { subscriptionService } from '@/services/api/subscription.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { SubscriptionPlan, StripePrice } from '@/types/subscription';
import { formatCurrency, type Currency } from '@/utils/currency';

interface PricingTableProps {
  currentPriceId?: string;
  currency?: string;
  interval?: 'month' | 'year';
  isSubscribed?: boolean;
  className?: string;
  /**
   * When true, the primary CTA always routes to `/register` regardless of auth state
   * and never opens Stripe Checkout. Use this for landing/marketing pages.
   */
  signupMode?: boolean;
}

export function PricingTable({
  currentPriceId,
  currency = 'eur',
  interval: initialInterval = 'year',
  isSubscribed = false,
  className = '',
  signupMode = false
}: PricingTableProps) {
  const { i18n, t } = useTranslation('plans');
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>(initialInterval);
  const [switchingToPriceId, setSwitchingToPriceId] = useState<string | null>(null);

  // Helper function to get plan data from translations based on plan_code
  const getPlanTranslation = (planCode: string) => {
    const planKey = planCode?.toLowerCase() || 'basic';
    return {
      name: t(`${planKey}.name`),
      description: t(`${planKey}.description`),
      features: t(`${planKey}.features`, { returnObjects: true }) || []
    };
  };

  // Fetch all plans
  const { data: plans, isLoading } = useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: () => subscriptionService.getStripePlans(),
  });

  const handleSwitchPlan = async (priceId: string) => {
    setSwitchingToPriceId(priceId);
    try {
      const url = await subscriptionService.getSwitchPlanUrl(priceId, i18n.language);
      window.open(url, '_blank');
      setSwitchingToPriceId(null);
    } catch (error) {
      console.error('Failed to get switch plan URL:', error);
      setSwitchingToPriceId(null);
    }
  };

  const handleStartTrial = async (priceId: string) => {
    // Landing pages: always go to the sign-up form, never open Stripe
    if (signupMode) {
      setLocation('/register');
      return;
    }

    // If not authenticated, redirect to sign up
    if (!isAuthenticated) {
      setLocation('/register');
      return;
    }

    setSwitchingToPriceId(priceId);
    try {
      // Create a checkout session for the selected plan
      const url = await subscriptionService.createCheckoutSession(priceId, i18n.language, currency);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setSwitchingToPriceId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No plans available
      </div>
    );
  }

  // Helper to get price for selected interval and currency
  const getPriceForInterval = (plan: SubscriptionPlan, interval: 'month' | 'year'): StripePrice | null => {
    // First try to find a price matching the requested currency
    const exactMatch = plan.prices.find(
      p => p.currency === currency &&
        p.recurring?.interval === interval &&
        p.type === 'recurring'
    );
    if (exactMatch) return exactMatch;

    // Fallback: return the first price matching the interval regardless of currency
    return plan.prices.find(
      p => p.recurring?.interval === interval &&
        p.type === 'recurring'
    ) || null;
  };

  // Helper to get the opposite interval price for showing "billed annually" text
  const getAnnualPrice = (plan: SubscriptionPlan): StripePrice | null => {
    if (selectedInterval === 'year') {
      return getPriceForInterval(plan, 'year');
    }
    return getPriceForInterval(plan, 'year');
  };

  // Helper to check if price is current
  const isCurrentPrice = (priceId: string) => {
    return currentPriceId === priceId;
  };

  // Helper to check if plan is recommended
  const isRecommended = (plan: SubscriptionPlan) => {
    return plan.metadata.recommended === 'true';
  };

  // Helper to check if in test mode
  const isTestMode = !import.meta.env.PROD || import.meta.env.VITE_STRIPE_MODE === 'test';

  // Calculate the savings percentage for yearly vs monthly billing
  const yearlySavingsPercent = (() => {
    if (!plans || plans.length === 0) return 20; // fallback
    // Use the first plan that has both monthly and yearly prices
    for (const plan of plans) {
      const monthlyPrice = getPriceForInterval(plan, 'month');
      const yearlyPrice = getPriceForInterval(plan, 'year');
      if (monthlyPrice && yearlyPrice) {
        const monthlyTotal = monthlyPrice.unitAmount * 12;
        const yearlyTotal = yearlyPrice.unitAmount;
        if (monthlyTotal > 0) {
          return Math.round((1 - yearlyTotal / monthlyTotal) * 100);
        }
      }
    }
    return 20; // fallback
  })();

  // Helper to detect contact-only plans (Stripe metadata flag — no price, mailto CTA)
  const isContactOnly = (plan: SubscriptionPlan) => plan.metadata?.contact_only === 'true';

  // Sort plans by price (lowest first) based on selected interval.
  // Contact-only plans are forced to the end regardless of price.
  const sortedPlans = plans ? [...plans].sort((a, b) => {
    const aContact = isContactOnly(a);
    const bContact = isContactOnly(b);
    if (aContact && !bContact) return 1;
    if (!aContact && bContact) return -1;

    const priceA = getPriceForInterval(a, selectedInterval);
    const priceB = getPriceForInterval(b, selectedInterval);

    if (!priceA) return 1;
    if (!priceB) return -1;

    return priceA.unitAmount - priceB.unitAmount;
  }) : [];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Interval Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          <button
            onClick={() => setSelectedInterval('month')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${selectedInterval === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            {t('pricing.monthly')}
          </button>
          <button
            onClick={() => setSelectedInterval('year')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${selectedInterval === 'year'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            {t('pricing.yearly')}
            {yearlySavingsPercent > 0 && (
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-0">
                {t('pricing.savePercent', { percent: yearlySavingsPercent })}
              </Badge>
            )}
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${sortedPlans.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
        {sortedPlans.map((plan) => {
          const contactOnly = isContactOnly(plan);
          const price = getPriceForInterval(plan, selectedInterval);
          const annualPrice = getAnnualPrice(plan);

          if (!price && !contactOnly) return null;

          const isCurrentPlan = price ? isCurrentPrice(price.id) : false;
          const isSwitching = price ? switchingToPriceId === price.id : false;
          const recommended = isRecommended(plan);

          // Get plan code from metadata and fetch translations
          const planCode = plan.metadata?.plan_code || plan.id;
          const planTranslation = getPlanTranslation(planCode);

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col bg-gray-50 border-0 ${isCurrentPlan ? 'ring-2 ring-primary' : ''
                }`}
            >
              <CardHeader className="space-y-3 pb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  {recommended && (
                    <Badge variant="secondary" className="text-xs">
                      Recommended
                    </Badge>
                  )}
                  {isTestMode && !contactOnly && (
                    <Badge className="bg-yellow-500 text-black text-xs font-semibold">
                      TEST MODE
                    </Badge>
                  )}
                  {isTestMode && contactOnly && (
                    <Badge aria-hidden="true" className="invisible bg-yellow-500 text-black text-xs font-semibold">
                      TEST MODE
                    </Badge>
                  )}
                  {isCurrentPlan && (
                    <Badge variant="default" className="text-xs">
                      Current Plan
                    </Badge>
                  )}
                </div>

                <div>
                  <CardTitle className="text-xl font-bold">
                    {planTranslation.name}
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-600 mt-1">
                    {planTranslation.description}
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="flex-1 space-y-6">
                {/* Pricing — contact-only plans render an invisible placeholder of the same shape so the CTA aligns vertically with sibling cards */}
                {contactOnly ? (
                  <div aria-hidden="true" className="invisible">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">&nbsp;</span>
                      <span className="text-sm">&nbsp;</span>
                    </div>
                    {selectedInterval === 'year' && (
                      <p className="text-sm mt-1">&nbsp;</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-gray-900">
                        {formatCurrency(
                          selectedInterval === 'year'
                            ? (price!.unitAmount / 100) / 12
                            : price!.unitAmount / 100,
                          price!.currency.toUpperCase() as Currency
                        )}
                      </span>
                      <span className="text-gray-600 text-sm">
                        {t('pricing.perMonth')}
                      </span>
                    </div>
                    {selectedInterval === 'year' && annualPrice && (
                      <p className="text-sm text-gray-600 mt-1">
                        {formatCurrency(price!.unitAmount / 100, price!.currency.toUpperCase() as Currency)} {t('pricing.billedAnnually')}
                      </p>
                    )}
                  </div>
                )}

                {/* CTA Button */}
                {contactOnly ? (
                  <div>
                    <a
                      href="mailto:sales@slotyapp.com"
                      className="inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 bg-brand-500 text-white hover:bg-brand-700 focus:ring-brand-500 px-4 py-2.5 text-base min-h-touch-target w-full"
                    >
                      {t('buttons.contactSales')}
                    </a>
                  </div>
                ) : !isCurrentPlan && (
                  <div>
                    <Button
                      onClick={() => isSubscribed ? handleSwitchPlan(price!.id) : handleStartTrial(price!.id)}
                      disabled={isSwitching}
                      className="w-full"
                    >
                      {isSwitching ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('buttons.loading')}
                        </>
                      ) : (
                        isSubscribed ? t('buttons.switchPlan') : t('buttons.startFreeTrial')
                      )}
                    </Button>
                  </div>
                )}

                {/* Features */}
                {Array.isArray(planTranslation.features) && planTranslation.features.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      {t('pricing.includes')}
                    </p>
                    <ul className="space-y-2">
                      {planTranslation.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default PricingTable;
